import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import styles from './EditableText.module.css';

interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** When true, stops rendering as a span and renders a permanent input */
  alwaysEditing?: boolean;
  /** Optional validator — reject the edit if it returns false */
  validate?: (value: string) => boolean;
}

/**
 * Click-to-edit text primitive. Renders as a span that swaps into an input
 * on click. Commits on Enter or blur, discards on Escape.
 */
export function EditableText({
  value,
  onChange,
  placeholder,
  className,
  alwaysEditing = false,
  validate,
}: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(alwaysEditing);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const commit = useCallback(() => {
    if (validate && !validate(draft)) {
      setDraft(value);
      setIsEditing(alwaysEditing);
      return;
    }
    if (draft !== value) onChange(draft);
    setIsEditing(alwaysEditing);
  }, [draft, value, onChange, validate, alwaysEditing]);

  const cancel = useCallback(() => {
    setDraft(value);
    setIsEditing(alwaysEditing);
  }, [value, alwaysEditing]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    },
    [commit, cancel]
  );

  if (isEditing || alwaysEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        placeholder={placeholder}
        className={`${styles.input} ${className ?? ''}`}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      className={`${styles.text} ${className ?? ''} ${
        !value ? styles.empty : ''
      }`}
      onClick={(e) => {
        e.stopPropagation();
        setIsEditing(true);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsEditing(true);
        }
      }}
    >
      {value || placeholder || '\u00A0'}
    </span>
  );
}
